import { useRef } from 'react'
import { motion, useMotionValue, useSpring, useTransform, useReducedMotion } from 'framer-motion'
import coinImg from '../../assets/images/heritage/hero-coin.jpg'

/**
 * A single real artifact — a British India "One Rupee" silver coin
 * (crown, wreath, Persian and English legend) — presented with a genuine
 * CSS 3D tilt: perspective + rotateX/rotateY driven by cursor position,
 * not a flat image. No WebGL, no 3D model: this is an honest depth
 * illusion built from one photograph in a real 3D transform space, which
 * is what's achievable without a model pipeline. The source photo is a
 * natural angled shot (not a top-down circular crop), so the frame uses
 * a soft radial vignette rather than a hard circular clip — it reads as
 * a spotlit object fading into the dark backdrop instead of an awkward
 * crop. Settles into a slow idle float when not hovered. Fully inert
 * under prefers-reduced-motion.
 */
const Hero3DCoin = () => {
  const stageRef = useRef(null)
  const reduceMotion = useReducedMotion()

  const mx = useMotionValue(0)
  const my = useMotionValue(0)
  const springX = useSpring(mx, { stiffness: 60, damping: 20 })
  const springY = useSpring(my, { stiffness: 60, damping: 20 })

  const rotateX = useTransform(springY, [-0.5, 0.5], [12, -12])
  const rotateY = useTransform(springX, [-0.5, 0.5], [-14, 14])
  const shadowX = useTransform(springX, [-0.5, 0.5], [16, -16])
  const shadowY = useTransform(springY, [-0.5, 0.5], [-10, 10])
  const shineX = useTransform(springX, [-0.5, 0.5], [30, 70])
  const shineY = useTransform(springY, [-0.5, 0.5], [30, 70])

  const handleMouseMove = (e) => {
    if (reduceMotion) return
    const el = stageRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    mx.set((e.clientX - rect.left) / rect.width - 0.5)
    my.set((e.clientY - rect.top) / rect.height - 0.5)
  }

  const handleMouseLeave = () => {
    mx.set(0)
    my.set(0)
  }

  const vignetteStyle = {
    WebkitMaskImage: 'radial-gradient(circle, black 52%, transparent 76%)',
    maskImage: 'radial-gradient(circle, black 52%, transparent 76%)',
  }

  return (
    <div
      ref={stageRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className="relative w-full max-w-[420px] mx-auto aspect-square perspective-stage"
    >
      {/* Spotlight glow behind the coin */}
      <div className="absolute inset-[-15%] pedestal-glow pointer-events-none" />

      {/* Drop shadow that shifts opposite the tilt for depth */}
      <motion.div
        className="absolute inset-10 rounded-full bg-black/50 blur-2xl"
        style={{ x: shadowX, y: shadowY }}
      />

      <motion.div
        className="relative w-full h-full preserve-3d"
        style={reduceMotion ? {} : { rotateX, rotateY }}
        animate={reduceMotion ? {} : { y: [0, -10, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
      >
        {/* Coin, softly vignetted rather than hard-clipped */}
        <div className="absolute inset-0" style={vignetteStyle}>
          <img
            src={coinImg}
            alt='British India "One Rupee" silver coin, crown and wreath design'
            className="w-full h-full object-cover"
            fetchPriority="high"
            decoding="async"
          />
          {/* Metallic sheen that tracks the tilt */}
          <motion.div
            className="absolute inset-0 pointer-events-none mix-blend-overlay"
            style={{
              background: useTransform(
                [shineX, shineY],
                ([x, y]) => `radial-gradient(circle at ${x}% ${y}%, rgba(255,255,255,0.5) 0%, transparent 45%)`
              ),
            }}
          />
        </div>
      </motion.div>

      {/* Caption plaque */}
      <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 translate-y-full pt-5 text-center whitespace-nowrap">
        <span className="block w-8 h-px bg-museum-antique-gold/40 mx-auto mb-3" />
        <p className="text-museum-ivory/40 font-sans text-[10px] uppercase tracking-[0.24em]">
          British India &middot; One Rupee &middot; Silver
        </p>
      </div>
    </div>
  )
}

export default Hero3DCoin
